/**
 * Admin External Users CRUD client.
 *
 * Routes through services/api/client.ts (apiGet/apiPost/apiDelete) because the
 * admin endpoints SHOULD invalidate the internal session on 401 (admin session
 * expiry → redirect to internal /login). The external auth surface uses a
 * dedicated, non-token-clearing fetch wrapper instead — see services/api/externalAuth.ts.
 */
import { apiGet, apiPost, apiDelete } from './client';

export interface ExternalUserInviter {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ExternalUserProjectBinding {
  id: number;
  name: string;
}

export type ExternalUserRowStatus = 'pending' | 'active' | 'locked' | 'deleted';

export interface ExternalUserRow {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  status: ExternalUserRowStatus;
  invitedAt: string; // ISO 8601
  lastLoginAt: string | null;
  invitedBy: ExternalUserInviter | null;
  projects: ExternalUserProjectBinding[];
}

export interface ExternalUsersListResponse {
  rows: ExternalUserRow[];
  total: number;
  page: number;
  perPage: number;
}

export interface InviteInput {
  email: string;
  firstName: string;
  lastName: string;
  projectIds: number[];
}

export interface ReplaceProjectsResponse {
  added: number[];
  removed: number[];
  current: number[];
}

export interface PickableProject {
  id: number;
  name: string;
}

export async function list(page = 1, perPage = 25): Promise<ExternalUsersListResponse> {
  const res = await apiGet<{ data: ExternalUsersListResponse }>(
    `/admin/external-users?page=${page}&perPage=${perPage}`,
  );
  return res.data;
}

export async function invite(input: InviteInput): Promise<{ id: number }> {
  const res = await apiPost<{ data: { id: number } }>('/admin/external-users/invite', input);
  return res.data;
}

export async function resendInvite(id: number): Promise<{ success: true }> {
  const res = await apiPost<{ data: { success: true } }>(
    `/admin/external-users/${id}/resend-invite`,
  );
  return res.data;
}

export async function softDelete(id: number): Promise<{ success: true }> {
  const res = await apiDelete<{ data: { success: true } }>(`/admin/external-users/${id}`);
  return res.data;
}

export async function replaceProjects(
  id: number,
  projectIds: number[],
): Promise<ReplaceProjectsResponse> {
  const res = await apiPost<{ data: ReplaceProjectsResponse }>(
    `/admin/external-users/${id}/projects`,
    { projectIds },
  );
  return res.data;
}

export async function listPickableProjects(): Promise<PickableProject[]> {
  const res = await apiGet<{ data: PickableProject[] }>(
    '/admin/external-users/pickable-projects',
  );
  return res.data;
}
